import Key from '~interfaces/key';
import { SkcDataResponse } from '~interfaces/responses';
import SkopeiConnectService from '~services/skopeiConnectService';

import KeyGroup from '../../../interfaces/keyGroup';

class KeyGroupService extends SkopeiConnectService {
	protected readonly path = 'key-groups';

	/**
	 * Get the keygroups that a given key is assigned to.
	 *
	 * @param keyId The id of the key.
	 * @param overwrittenClientId Filter on organisation id by header.
	 * @param signal Signal for aborting.
	 *
	 * @returns An array of KeyGroup that the given key id is assigned to.
	 */
	async getKeyGroupsByKeyId(
		params: {
			keyId: string;
			overwrittenClientId?: string;
		},
		signal?: AbortSignal
	): Promise<KeyGroup[]> {
		const endpoint = this.createEndpoint('v2', ['key', params.keyId]);
		return await this.client
			.get<KeyGroupResponse[]>(endpoint, {
				signal,
				headers:
					params.overwrittenClientId ?
						{ OverwrittenClientId: params.overwrittenClientId }
					:	undefined,
			})
			.then((response) => {
				return Promise.resolve(response.data.map(KeyGroupService.fromResponse));
			});
	}

	public getKeyGroupsByKeyIdCacheKey(
		keyId: string,
		overwrittenClientId?: string
	): (string | undefined)[] {
		return [this.path, 'key', keyId, overwrittenClientId];
	}

	/**
	 * Gets a Keygroup by id,
	 *
	 * @param keyGroupId The id of the key group.
	 * @param overwrittenClientId Filter on organisation id by header.
	 * @param signal Signal for aborting.
	 *
	 * @returns The KeyGroup for the given id.
	 */
	async getKeyGroupById(
		params: { keyGroupId: string; overwrittenClientId?: string },
		signal?: AbortSignal
	): Promise<KeyGroup> {
		const endpoint = this.createEndpoint('v1', [params.keyGroupId]);
		return await this.client
			.get<SkcDataResponse<KeyGroupResponse>>(endpoint, {
				signal: signal,
				headers:
					params.overwrittenClientId ?
						{ OverwrittenClientId: params.overwrittenClientId }
					:	undefined,
			})
			.then((response) => {
				return Promise.resolve(KeyGroupService.fromResponse(response.data.data));
			});
	}

	public getKeyGroupByIdCacheKey(
		keyGroupId?: string,
		overwrittenClientId?: string
	): (string | undefined)[] {
		return [this.path, keyGroupId, overwrittenClientId];
	}

	/**
	 * Model parsing.
	 */
	static fromResponse(response: KeyGroupResponse): KeyGroup {
		const { id, resourceGroupId } = response;
		return {
			...response,
			id: id.toString(),
			resourceGroupId: resourceGroupId?.toString() ?? undefined,
			keys: response.keys?.map(({ id, ...rest }) => {
				return {
					...rest,
					id: id.toString(),
				};
			}),
		};
	}
}

interface KeyGroupResponse {
	id: number;
	resourceGroupId: number;
	name: string;
	description?: string;
	keys?: Key[];
}

export default KeyGroupService;
